// Scratch-pad persistence: load after authentication, serialize saves and retain
// failed edits in memory until an explicit retry succeeds. Never cache vault text.
export function createQuickNotes(area: HTMLDivElement, status: HTMLElement, retry: HTMLButtonElement) {
  let ready = false;
  let dirty = false;
  let timer: number | undefined;
  let saving: Promise<boolean> | null = null;
  let loading: Promise<void> | null = null;
  let revision = 0;

  function message(text: string, failed = false) {
    status.textContent = text;
    retry.hidden = !failed;
  }
  async function request(method: string, content?: string) {
    const response = await fetch('/api/quicknotes', {
      method, credentials: 'same-origin',
      ...(content === undefined ? {} : {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content_html: content }),
        keepalive: new Blob([content]).size < 60000,
      }),
    });
    if (!response.ok) throw new Error(`Request failed (${response.status})`);
    return response;
  }
  function save(): Promise<boolean> {
    window.clearTimeout(timer);
    if (saving) return saving;
    if (!dirty) return Promise.resolve(true);
    saving = (async () => {
      try {
        while (dirty) {
          const version = revision;
          message('Saving…');
          await request('PUT', area.innerHTML);
          if (version === revision) dirty = false;
        }
        message('Saved');
        return true;
      } catch {
        message('Not saved — keep this page open and retry.', true);
        return false;
      } finally { saving = null; }
    })();
    return saving;
  }
  function changed() {
    dirty = true;
    revision++;
    message('Unsaved changes');
    window.clearTimeout(timer);
    timer = window.setTimeout(() => void save(), 500);
  }
  async function load(sanitize: (html: string) => string) {
    if (dirty || saving) return;
    if (loading) return loading;
    const version = revision;
    loading = (async () => {
      try {
        const response = await request('GET');
        const data = await response.json();
        if (typeof data.content_html !== 'string') throw new Error('Invalid scratch pad response');
        if (revision !== version) return;
        area.innerHTML = sanitize(data.content_html);
        ready = true;
        area.contentEditable = 'true';
        message('Saved');
      } catch { message('Could not load Quick Notes. Retry to edit.', true); }
      finally { loading = null; }
    })();
    return loading;
  }
  area.contentEditable = 'false';
  message('Loading…');
  area.addEventListener('input', changed);
  area.addEventListener('blur', () => void save());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void save();
  });
  window.addEventListener('beforeunload', event => { if (dirty) event.preventDefault(); });
  return { load, save, changed, get ready() { return ready; }, get dirty() { return dirty; } };
}
